import { createStore, type ReactStore } from '@w/react-store'
import type { SocketLike } from './memory-host'
import { MAX_EDIT_MESSAGE_BYTES, POLICY_VIOLATION, PROTOCOL_VERSION, type LiveEdit, type Peer, type PresenceState, type ServerMessage } from './protocol'

/** Reconnect delay before the first retry; doubles per retry up to {@link MAX_BACKOFF_MS}. */
export const MIN_BACKOFF_MS = 500
export const MAX_BACKOFF_MS = 30_000
/** Shortest gap between two `state` frames sent by `setLocal`. */
export const SEND_THROTTLE_MS = 100
/** The client resends its local state this often, so receivers do not expire it. */
export const RENEW_MS = 15_000
/** A receiver drops a peer it has not heard from for longer than this. */
export const PEER_TTL_MS = 30_000

export type RealtimeStatus = 'connecting' | 'online' | 'offline' | 'closed'

/** Tab visibility; defaults to `document.visibilityState` when a document exists. */
export interface Visibility {
  hidden(): boolean
  /** Calls `listener` on every change; returns the unsubscribe. */
  onChange(listener: () => void): () => void
}

export interface RealtimeClientOptions {
  /** Opens a new socket to the room: an in-memory host socket or a browser `WebSocket`. */
  connect: () => SocketLike
  visibility?: Visibility
  /** Picks the point inside the backoff range; `Math.random` by default. */
  random?: () => number
  /** Runs one pull after a peer's poke; at most one call is in flight (docs/use-cases/realtime.spec.md, Client). */
  onPoke?: () => Promise<unknown>
  /** Runs after every welcome, so the caller pulls once for pokes it missed while disconnected. */
  onReconnect?: () => void
  onEdit?: (edit: LiveEdit & { connId: string; userId: string }) => void
}

export type ReceivedLiveEdit = LiveEdit & { connId: string; userId: string }

export interface RealtimeClient {
  setLocal(state: PresenceState): void
  sendEdit(edit: LiveEdit): boolean
  supportsEdits(): boolean
  /** Consumes pending render work, never a durable acknowledgement. */
  acknowledgeEdits(edits: readonly ReceivedLiveEdit[]): void
  poke(): void
  close(): void
}

interface ClientState {
  status: RealtimeStatus
  peers: Peer[]
  edits: readonly ReceivedLiveEdit[]
}

const STORE = Symbol('realtime-store')

interface ClientImpl extends RealtimeClient {
  [STORE]: ReactStore<ClientState>
}

/** The store the React hooks read; not part of the public client type (react-state rule 0). */
export function storeOf(client: RealtimeClient): ReactStore<ClientState> {
  return (client as ClientImpl)[STORE]
}

const editEncoder = new TextEncoder()

const LEFT: PresenceState = { pageId: null, sel: null, active: false }

function documentVisibility(): Visibility | undefined {
  if (typeof document === 'undefined') return undefined
  return {
    hidden: () => document.visibilityState === 'hidden',
    onChange(listener) {
      document.addEventListener('visibilitychange', listener)
      return () => {
        document.removeEventListener('visibilitychange', listener)
      }
    },
  }
}

export function createRealtimeClient({
  connect,
  visibility = documentVisibility(),
  random = Math.random,
  onPoke,
  onReconnect,
  onEdit,
}: RealtimeClientOptions): RealtimeClient {
  const store = createStore<ClientState>({ status: 'connecting', peers: [], edits: [] })
  /** `Date.now()` each peer was last heard from. */
  const heard = new Map<string, number>()
  let socket: SocketLike | null = null
  let online = false
  let editsSupported = false
  let closed = false
  let retries = 0
  let local: PresenceState | null = null
  let lastSentAt = -Infinity
  let lastSentPage: string | null | undefined
  let reconnectTimer: ReturnType<typeof setTimeout> | undefined
  let throttleTimer: ReturnType<typeof setTimeout> | undefined
  let renewTimer: ReturnType<typeof setInterval> | undefined
  let expiryTimer: ReturnType<typeof setTimeout> | undefined
  let pokeRunning = false
  let pokeQueued = false

  const hidden = () => visibility?.hidden() ?? false

  const setPeers = (update: (peers: Peer[]) => Peer[]) => {
    store.update((prev) => ({ ...prev, peers: update(prev.peers) }))
  }

  const clearPeers = () => {
    heard.clear()
    clearTimeout(expiryTimer)
    if (store.getState().peers.length > 0) setPeers(() => [])
  }

  const scheduleExpiry = () => {
    clearTimeout(expiryTimer)
    if (heard.size === 0) return
    const due = Math.min(...heard.values()) + PEER_TTL_MS + 1
    expiryTimer = setTimeout(
      () => {
        const oldest = Date.now() - PEER_TTL_MS
        const silent = new Set([...heard].filter(([, at]) => at < oldest).map(([connId]) => connId))
        for (const connId of silent) heard.delete(connId)
        if (silent.size > 0) setPeers((peers) => peers.filter((p) => !silent.has(p.connId)))
        scheduleExpiry()
      },
      Math.max(0, due - Date.now()),
    )
  }

  const send = (msg: object) => {
    if (online) socket?.send(JSON.stringify({ v: PROTOCOL_VERSION, ...msg }))
  }

  /** Sends what peers should see now, bypassing the throttle. */
  const sendState = () => {
    clearTimeout(throttleTimer)
    throttleTimer = undefined
    if (!online) return
    const state = hidden() ? LEFT : local
    if (!state) return
    lastSentPage = state.pageId
    lastSentAt = Date.now()
    send({ t: 'state', state })
  }

  const runPoke = () => {
    if (!onPoke) return
    if (pokeRunning) {
      pokeQueued = true
      return
    }
    pokeRunning = true
    const settle = () => {
      pokeRunning = false
      if (!pokeQueued || closed) return
      pokeQueued = false
      runPoke()
    }
    onPoke().then(settle, settle)
  }

  const receive = (msg: ServerMessage) => {
    if (msg.t === 'welcome') {
      editsSupported = msg.edits === true
      online = true
      retries = 0
      store.update((prev) => ({ ...prev, status: 'online' }))
      sendState()
      onReconnect?.()
      return
    }
    if (msg.t === 'edit') {
      if (!hidden() && local?.pageId === msg.pageId) {
        store.update(prev => ({ ...prev, edits: [...prev.edits, msg] }))
        onEdit?.(msg)
      }
      return
    }
    if (msg.t === 'poke') {
      runPoke()
      return
    }
    if (msg.t === 'leave') {
      heard.delete(msg.connId)
      setPeers((peers) => peers.filter((p) => p.connId !== msg.connId))
      scheduleExpiry()
      return
    }
    const now = Date.now()
    if (msg.t === 'snapshot') {
      heard.clear()
      for (const peer of msg.peers) heard.set(peer.connId, now)
      setPeers(() => msg.peers)
    } else {
      const { peer } = msg
      heard.set(peer.connId, now)
      setPeers((peers) =>
        peers.some((p) => p.connId === peer.connId)
          ? peers.map((p) => (p.connId === peer.connId ? peer : p))
          : [...peers, peer],
      )
    }
    scheduleExpiry()
  }

  const retry = () => {
    if (closed) return
    online = false
    clearPeers()
    store.update((prev) => ({ ...prev, status: 'offline' }))
    clearTimeout(reconnectTimer)
    const base = Math.min(MAX_BACKOFF_MS, MIN_BACKOFF_MS * 2 ** retries)
    retries += 1
    reconnectTimer = setTimeout(open, base / 2 + (random() * base) / 2)
  }

  const open = () => {
    if (closed) return
    let next: SocketLike
    try { next = connect() }
    catch { retry(); return }
    socket = next
    // oxlint-disable-next-line unicorn/prefer-add-event-listener -- SocketLike is the WebSocket on* subset
    next.onmessage = (event) => {
      if (socket !== next || typeof event.data !== 'string') return
      let msg: ServerMessage
      try {
        msg = JSON.parse(event.data) as ServerMessage
      } catch {
        return
      }
      if (msg.v === PROTOCOL_VERSION) receive(msg)
    }
    // oxlint-disable-next-line unicorn/prefer-add-event-listener -- SocketLike is the WebSocket on* subset
    next.onclose = (event) => {
      if (socket !== next) return
      socket = null
      online = false
      clearPeers()
      if (closed) return
      // Refused (403) or a policy violation: reconnecting would be refused again (realtime.spec.md, Client).
      if (event.code === POLICY_VIOLATION) {
        client.close()
        return
      }
      retry()
    }
  }

  const stopVisibility = visibility?.onChange(sendState)
  renewTimer = setInterval(() => {
    if (!hidden()) sendState()
  }, RENEW_MS)
  const client: ClientImpl = {
    [STORE]: store,

    setLocal(state) {
      if (local?.pageId !== state.pageId) store.update(prev => ({ ...prev, edits: prev.edits.filter(edit => edit.pageId === state.pageId) }))
      local = state
      if (throttleTimer !== undefined) return
      const wait = lastSentAt + SEND_THROTTLE_MS - Date.now()
      if (wait <= 0) sendState()
      else throttleTimer = setTimeout(sendState, wait)
    },

    supportsEdits: () => online && !hidden() && editsSupported,

    sendEdit(edit) {
      if (!online || hidden() || local?.pageId !== edit.pageId || !edit.pageId || !edit.update) return false
      const frame = JSON.stringify({ v: PROTOCOL_VERSION, t: 'edit', pageId: edit.pageId, update: edit.update })
      if (frame.length * 3 > MAX_EDIT_MESSAGE_BYTES && editEncoder.encode(frame).byteLength > MAX_EDIT_MESSAGE_BYTES) return false
      if (lastSentPage !== edit.pageId) sendState()
      socket?.send(frame)
      return true
    },

    acknowledgeEdits(edits) {
      const consumed = new Set(edits)
      store.update(prev => ({ ...prev, edits: prev.edits.filter(edit => !consumed.has(edit)) }))
    },

    poke() {
      send({ t: 'poke' })
    },

    close() {
      if (closed) return
      closed = true
      clearTimeout(reconnectTimer)
      clearTimeout(throttleTimer)
      clearInterval(renewTimer)
      renewTimer = undefined
      stopVisibility?.()
      const last = socket
      socket = null
      online = false
      last?.close(1000, 'closed')
      clearPeers()
      store.update((prev) => ({ ...prev, status: 'closed', edits: [] }))
    },
  }
  open()
  return client
}
